import { Checkbox, Divider, Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { ContentCard } from "@shared";
import { FormikProps } from "formik";

import { DayOfWeek } from "@/api";
import { HoursInputField } from "@/pages/system-settings/tabs/counselors/pages/counselor-detail/components/hours-input-field/HoursInputField";
import { CounselorDutyPreferencesFormValues } from "@/pages/system-settings/tabs/counselors/pages/counselor-detail/pages/duty-preferences/CounselorDutyPreferences.types";
import { useTranslation } from "@/translations";

interface Props {
  formik: FormikProps<CounselorDutyPreferencesFormValues>;
}

const WEEKDAY_LABEL_KEYS = {
  [DayOfWeek.Monday]: "counselors.dutyPreferences.duties.weekdays.monday",
  [DayOfWeek.Tuesday]: "counselors.dutyPreferences.duties.weekdays.tuesday",
  [DayOfWeek.Wednesday]: "counselors.dutyPreferences.duties.weekdays.wednesday",
  [DayOfWeek.Thursday]: "counselors.dutyPreferences.duties.weekdays.thursday",
  [DayOfWeek.Friday]: "counselors.dutyPreferences.duties.weekdays.friday",
} as const satisfies Partial<Record<DayOfWeek, string>>;

const DUTY_COLUMNS = [
  { field: "aw12", labelKey: "counselors.dutyPreferences.dutyTypes.aw12" },
  { field: "aw3", labelKey: "counselors.dutyPreferences.dutyTypes.aw3" },
  { field: "bg", labelKey: "counselors.dutyPreferences.dutyTypes.bg" },
] as const;

const HoursLabelWidth = 250;

export function DutyPreferencesCard({ formik }: Props) {
  const { t } = useTranslation();

  return (
    <ContentCard title={t("counselors.dutyPreferences.duties.title")}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("counselors.dutyPreferences.duties.weekday")}</TableCell>
            {DUTY_COLUMNS.map((column) => (
              <TableCell key={column.field} align="center">
                {t(column.labelKey)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {formik.values.dutyPreferences.map((duty, index) => {
            const weekdayLabel = t(WEEKDAY_LABEL_KEYS[duty.weekday as keyof typeof WEEKDAY_LABEL_KEYS]);
            return (
              <TableRow key={duty.weekday}>
                <TableCell component="th">{weekdayLabel}</TableCell>
                {DUTY_COLUMNS.map((column) => (
                  <TableCell key={column.field} align="center">
                    <Checkbox
                      checked={duty[column.field]}
                      onChange={(_, checked) =>
                        void formik.setFieldValue(`dutyPreferences.${index}.${column.field}`, checked)
                      }
                      slotProps={{ input: { "aria-label": `${weekdayLabel} ${t(column.labelKey)}` } }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Divider />

      <Stack direction="column" spacing={2} alignItems="flex-start">
        <HoursInputField
          formik={formik}
          fieldName="courtHours"
          label={t("counselors.dutyPreferences.duties.courtHours")}
          unit={t("counselors.dutyPreferences.duties.hoursUnit")}
          labelWidth={HoursLabelWidth}
        />
        <HoursInputField
          formik={formik}
          fieldName="awHours"
          label={t("counselors.dutyPreferences.duties.awHours")}
          unit={t("counselors.dutyPreferences.duties.hoursUnit")}
          labelWidth={HoursLabelWidth}
        />
      </Stack>
    </ContentCard>
  );
}
